/**
 * @fileoverview Login page
 * @description Athletic-themed trainer authentication with dramatic visuals
 * @module routes/auth/Login
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/useToast';
import { Zap, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Login(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/');
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to sign in',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen flex overflow-hidden">
      {/* Calm light background — grid only, no orbs/gradients */}
      <div className="absolute inset-0 bg-background">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
      </div>

      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
        <div className="relative z-10 max-w-lg animate-fade-in-up">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-energy shadow-xl shadow-primary/30">
              <Zap className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="heading-display text-5xl font-bold tracking-tight">REPKEEP</h1>
              <p className="text-muted-foreground -mt-1">Trainer Hub</p>
            </div>
          </div>

          {/* Tagline */}
          <h2 className="heading-display text-4xl lg:text-5xl font-bold leading-tight mb-6">
            <span className="text-gradient">ELEVATE</span>
            <br />
            YOUR COACHING
          </h2>
          <p className="text-lg text-muted-foreground max-w-md">
            Transform your training business with AI-powered insights, seamless client management, and data-driven programming.
          </p>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-3 gap-8">
            {[
              { value: '10K+', label: 'Active Trainers' },
              { value: '500K+', label: 'Workouts Logged' },
              { value: '98%', label: 'Client Retention' },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={cn('animate-fade-in-up', `animate-delay-${(index + 2) * 100}`)}
              >
                <p className="text-3xl font-bold text-gradient">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 animate-fade-in-up">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-energy">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="heading-display text-2xl font-bold tracking-tight">REPKEEP</h1>
              <p className="text-xs text-muted-foreground -mt-0.5">Trainer Hub</p>
            </div>
          </div>

          {/* Form card */}
          <div className="stat-card p-8 animate-fade-in-up animate-delay-100">
            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
              <p className="text-muted-foreground mt-1">
                Sign in to continue to your dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={cn(
                    'h-12 bg-accent/50 border-border/50',
                    'focus:bg-accent focus:border-primary/50',
                    'transition-all'
                  )}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={cn(
                      'h-12 bg-accent/50 border-border/50 pr-12',
                      'focus:bg-accent focus:border-primary/50',
                      'transition-all'
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={cn(
                  'w-full h-12 text-base font-semibold',
                  'bg-gradient-energy hover:opacity-90 transition-opacity',
                  'shadow-lg shadow-primary/20'
                )}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-4 text-muted-foreground">
                  New to RepKeep?
                </span>
              </div>
            </div>

            {/* Register link */}
            <Link
              to="/register"
              className={cn(
                'flex items-center justify-center gap-2 w-full h-12',
                'rounded-lg border border-border/50 text-sm font-medium',
                'hover:border-primary/50 hover:bg-primary/5 transition-all'
              )}
            >
              Create an account
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-8 animate-fade-in-up animate-delay-300">
            By signing in, you agree to our{' '}
            <a
              href="https://repkeep.app/terms"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://repkeep.app/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
